import { useState } from 'react';
import { Globe } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

/**
 * TimezoneConfirm — small inline control rendered on the confirm step of an
 * ONLINE booking. Surfaces both the tenant's timezone (where the salon's
 * calendar lives) and the customer's detected timezone, and lets the
 * customer override the latter via a short list of common IANA zones.
 *
 * Marketplace variant: i18n via react-i18next. Mirrors the client-booking
 * control but uses the lucide Globe icon and Trans for the tz-note copy so
 * the translated string can keep its <strong> emphasis.
 */

interface TimezoneConfirmProps {
  tenantTimezone: string;
  customerTimezone: string;
  onChange: (tz: string) => void;
}

// Curated short list. Covers EU + Americas + LATAM + APAC without
// overwhelming the dropdown — the BE accepts any valid IANA id so the
// long tail can still be served by future work.
const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'America/Mexico_City',
  'America/Argentina/Buenos_Aires',
  'Europe/London',
  'Europe/Paris',
  'Europe/Brussels',
  'Europe/Madrid',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'Europe/Lisbon',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
  'UTC',
];

export function TimezoneConfirm({
  tenantTimezone,
  customerTimezone,
  onChange,
}: TimezoneConfirmProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  // Always keep the customer's current zone in the options — both for
  // unusual Intl outputs and so a revisited selection round-trips.
  const options = Array.from(new Set([customerTimezone, ...COMMON_TIMEZONES]));

  return (
    <div className="flex items-start gap-2 p-3 rounded-xl bg-secondary/60 border border-border/40">
      <Globe className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" aria-hidden="true" />
      <div className="flex-1 text-xs text-foreground leading-relaxed">
        <Trans
          i18nKey="booking.confirm.online.tzNote"
          values={{ tenantTz: tenantTimezone, customerTz: customerTimezone }}
          components={{ 1: <strong /> }}
        />{' '}
        {editing ? (
          <select
            value={customerTimezone}
            onChange={(e) => {
              onChange(e.target.value);
              setEditing(false);
            }}
            className="ml-1 text-xs border border-border/60 rounded px-1 py-0.5 bg-card"
            aria-label={t('booking.confirm.online.timezoneEdit')}
          >
            {options.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="ml-1 text-primary hover:underline underline-offset-4 font-medium"
          >
            {t('booking.confirm.online.timezoneEdit')}
          </button>
        )}
      </div>
    </div>
  );
}
